import React, { useEffect, useRef, useState, useImperativeHandle, forwardRef } from "react";
import "./TimePartsInput.css";

/**
 * 自前 2 分割時刻入力 (HH / MM)
 *
 * `<input type="time">` を使わない。iOS Safari のドラム式ピッカーは
 * 開いた時点で値が HH:MM に揃うため「入力途中」を判別できず、
 * 選択のたびに誤操作が起きる。日付の DatePartsInput と同じく、
 * 数字を直接打つ方式にして OS のピッカーに依存しない。
 *
 * 仕様:
 *  - 時 2 桁完成 -> 分へ自動フォーカス
 *  - 分 2 桁完成 -> その場で止まる (次フィールドへの自動遷移はしない)
 *  - 24 時制。範囲外の値はそもそも入力できない
 *      時: 1 桁目が 3〜9 なら "09" のように 0 埋めして確定し分へ進む
 *          "2" の次に 4〜9 を打つと、その数字を新しい時として扱う ("25" -> "05")
 *      分: 1 桁目が 6〜9 なら "06" のように 0 埋めして確定する
 *  - 1 桁のまま離れた場合は 0 埋めして確定する ("9" -> "09")
 *  - 空欄で Backspace -> 1 つ前のセグメントへフォーカス
 *  - ":" キーで次のセグメントへ
 *  - 親には完成した "HH:MM" のみ onChange で通知 (途中状態は通知しない)
 *  - 空に戻したときは "" を通知
 *
 * props:
 *   value     : "HH:MM" または ""
 *   onChange  : (value: string) => void
 *   onBlur    : (e: { target: { value: string, name: string } }) => void  // 任意
 *   required  : boolean
 *   disabled  : boolean
 *   className : string  追加クラス
 *   name      : string  HTML name (任意)
 *   label     : string  読み上げ用の名称。「出勤」なら「出勤 時」「出勤 分」になる
 */
const TimePartsInput = forwardRef(function TimePartsInput(
  { value, onChange, onBlur, required, disabled, className, name, label },
  ref
) {
  const [hourStr, setHourStr] = useState("");
  const [minStr, setMinStr] = useState("");

  const hourRef = useRef(null);
  const minRef = useRef(null);
  const wrapperRef = useRef(null);

  useImperativeHandle(ref, () => ({
    focus: () => hourRef.current?.focus(),
  }));

  useEffect(() => {
    if (!value) {
      setHourStr("");
      setMinStr("");
      return;
    }
    const m = /^(\d{1,2}):(\d{2})/.exec(String(value));
    if (m) {
      setHourStr(m[1].padStart(2, "0"));
      setMinStr(m[2]);
    }
  }, [value]);

  const emit = (h, mi) => {
    if (h.length === 2 && mi.length === 2) {
      onChange(`${h}:${mi}`);
    } else if (!h && !mi) {
      onChange("");
    }
  };

  const handleHour = (e) => {
    const digits = e.target.value.replace(/\D/g, "").slice(0, 2);
    let next = digits;
    let done = false;

    if (digits.length === 1) {
      // 3〜9 は 2 桁目がありえない（23 時までのため）ので、その場で確定する
      if (Number(digits) >= 3) {
        next = `0${digits}`;
        done = true;
      }
    } else if (digits.length === 2) {
      if (Number(digits) > 23) {
        // "25" のような入力は、後ろの数字を新しい時として扱う（ブラウザ標準と同じ挙動）
        next = `0${digits[1]}`;
      }
      done = true;
    }

    setHourStr(next);
    emit(next, minStr);
    if (done) {
      minRef.current?.focus();
      minRef.current?.select();
    }
  };

  const handleMin = (e) => {
    const digits = e.target.value.replace(/\D/g, "").slice(0, 2);
    let next = digits;

    // 6〜9 は 2 桁目がありえない（59 分までのため）ので、その場で確定する
    if (digits.length === 1 && Number(digits) >= 6) {
      next = `0${digits}`;
    }

    setMinStr(next);
    emit(hourStr, next);
    // 分が埋まっても次へは移らない。iOS でフォーカスが飛ぶと入力が中断されるため。
  };

  const handleBlur = (e) => {
    if (wrapperRef.current && e.relatedTarget && wrapperRef.current.contains(e.relatedTarget)) {
      return;
    }
    // 1 桁のまま離れた場合は 0 埋めして確定する（"9" -> "09"）
    const h = hourStr.length === 1 ? hourStr.padStart(2, "0") : hourStr;
    const mi = minStr.length === 1 ? minStr.padStart(2, "0") : minStr;
    if (h !== hourStr) setHourStr(h);
    if (mi !== minStr) setMinStr(mi);

    const composed = h.length === 2 && mi.length === 2 ? `${h}:${mi}` : "";
    if (composed && composed !== value) onChange(composed);

    if (onBlur) {
      onBlur({ target: { value: composed, name: name || "" } });
    }
  };

  const handleKey = (which) => (e) => {
    if (e.key === "Backspace" && e.target.value === "" && which === "min") {
      e.preventDefault();
      hourRef.current?.focus();
    } else if (e.key === ":" && which === "hour") {
      e.preventDefault();
      minRef.current?.focus();
      minRef.current?.select();
    } else if (e.key === "ArrowRight" && which === "hour" && e.target.selectionStart === e.target.value.length) {
      e.preventDefault();
      minRef.current?.focus();
      minRef.current?.select();
    } else if (e.key === "ArrowLeft" && which === "min" && e.target.selectionStart === 0) {
      e.preventDefault();
      hourRef.current?.focus();
    }
  };

  return (
    <div
      ref={wrapperRef}
      className={`time-parts-input${disabled ? " tp-disabled" : ""} ${className || ""}`}
      onBlur={handleBlur}
    >
      <input
        ref={hourRef}
        type="text"
        inputMode="numeric"
        value={hourStr}
        onChange={handleHour}
        onKeyDown={handleKey("hour")}
        onFocus={(e) => e.target.select()}
        placeholder="--"
        className="tp-hour"
        maxLength={2}
        required={required}
        disabled={disabled}
        aria-label={label ? `${label} 時` : "時"}
      />
      <span className="tp-sep">:</span>
      <input
        ref={minRef}
        type="text"
        inputMode="numeric"
        value={minStr}
        onChange={handleMin}
        onKeyDown={handleKey("min")}
        onFocus={(e) => e.target.select()}
        placeholder="--"
        className="tp-min"
        maxLength={2}
        required={required}
        disabled={disabled}
        aria-label={label ? `${label} 分` : "分"}
      />
      {name && <input type="hidden" name={name} value={hourStr && minStr ? `${hourStr}:${minStr}` : ""} />}
    </div>
  );
});

export default TimePartsInput;
